
import { Component, Vue } from "vue-property-decorator";
import AddButton from "@/components/buttons/AddButton.vue";
import InfoDialog from "@/components/dialogs/InfoDialog.vue";
import StationsContainerFrame from "@/components/station/StationsContainerFrame.vue";
import { StationSummary } from "@/api/dtos/StationSummary.schema";
import { StationsModule } from "@/store/modules/StationsModule";
import { OperatorSummary } from "@/api/dtos/Station.schema";
import { OperatorsModule } from "@/store/modules/OperatorsModule";
import { User } from "@/api/dtos/User.schema";
import { UsersModule } from "@/store/modules/UsersModule";

@Component({
  components: {
    StationsContainerFrame,
    AddButton,
    InfoDialog,
  },
})
export default class StationsOverView extends Vue {
  filter = "";
  onlyShowPlatformService = false;
  showPlatformServiceDialog = false;

  mounted(): void {
    OperatorsModule.fetchOperatorSummaries();
    StationsModule.fetchStationSummaries();
  }

  get operatorSummaries(): OperatorSummary[] {
    return OperatorsModule.operatorSummaries;
  }

  get localUser(): User | null {
    return UsersModule.localUser;
  }

  get isAdmin(): boolean {
    return this.localUser == null ? false : this.localUser.isAdmin;
  }

  get operatorIdToOperatorMap(): Map<string, OperatorSummary> {
    var map: Map<string, OperatorSummary> = new Map<string, OperatorSummary>();
    for (const operator of OperatorsModule.operatorSummaries) {
      map.set(operator.uuid, operator);
    }
    return map;
  }

  private scoreStation(station: StationSummary): number {
    if (!this.filter) return 0;
    let score = 0;
    const f = this.filter.toLowerCase();
    if (station.platformServiceActive && this.onlyShowPlatformService)
      score += 0xf;
    if (station.stationLocation.stationName.toLowerCase().includes(f))
      score += 0x8;
    if (station.stationLocation.address.city.toLowerCase().includes(f))
      score += 0x4;
    if (station.stationLocation.address.street.toLowerCase().includes(f))
      score += 0x2;
    if (station.stationLocation.address.postalCode.toLowerCase().includes(f))
      score += 0x1;
    return score;
  }

  private scoreOperator(operator: OperatorSummary): number {
    if (!this.filter) return 0;
    let score = 0;
    const f = this.filter.toLowerCase();
    if (operator.platformServiceActive && this.onlyShowPlatformService)
      score += 0xf;
    if (operator.name.toLowerCase().includes(f)) score += 0xf;
    return score;
  }

  get rankedOperators(): {
    operatorSummary: OperatorSummary;
    stations: StationSummary[];
    score: number;
  }[] {
    const stationSummaries = StationsModule.stationSummaries;

    return this.operatorSummaries
      .map((operator) => {
        const stations = stationSummaries.filter(
          (s) => s.operator.uuid === operator.uuid
        );

        const scoredStations = stations.map((station) => ({
          station,
          score: this.scoreStation(station),
        }));

        const score =
          scoredStations.reduce((sum, s) => sum + s.score, 0) +
          this.scoreOperator(operator);

        return {
          operatorSummary: operator,
          stations: scoredStations
            .sort((a, b) => b.score - a.score)
            .map((s) => s.station)
            .filter(
              (s) => s.platformServiceActive || !this.onlyShowPlatformService
            ),
          score,
        };
      })
      .filter((o) => {
        const user = this.localUser;
        if (!user) return false;

        if (user.isAdmin) return true;

        const userId = user.uuid;
        const managerOf = user.managerOf || [];
        const ownerOf = user.ownerOf || [];

        // Owner if explicitly listed in operator.owners, OR own any station inside that operator.
        const isOwnerOfOperator =
          Array.isArray(o.operatorSummary.owners) &&
          o.operatorSummary.owners.includes(userId);

        const isOwnerOfStation = o.stations.some((s) =>
          ownerOf.includes(s.uuid)
        );

        if (isOwnerOfOperator || isOwnerOfStation) return true;

        // Manager if manage any station inside that operator.
        const managesStationInOperator = o.stations.some((s) =>
          managerOf.includes(s.uuid)
        );

        if (managesStationInOperator) return true;

        return false;
      })
      .filter((o) => o.score > 0 || !this.filter) // hide zero-score entries if filtering
      .sort((a, b) => b.score - a.score);
  }
}
